import React, { useState, useEffect } from 'react';
import { Channel } from '../../api/channels';
import { colors, spacing, typography, typographyFlat, borderRadius, shadows } from '../../styles/tokens';

interface ChannelConfigDrawerProps {
  channel: Channel | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updated: Partial<Channel>) => void;
}

export const ChannelConfigDrawer: React.FC<ChannelConfigDrawerProps> = ({ channel, isOpen, onClose, onSave }) => {
  const [vaultPath, setVaultPath] = useState('');
  const [syncEnabled, setSyncEnabled] = useState<boolean>(true);
  const [inboundEnabled, setInboundEnabled] = useState<boolean>(true);
  const [outboundEnabled, setOutboundEnabled] = useState<boolean>(true);
  const [ordersWebhookEnabled, setOrdersWebhookEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (channel) {
      setVaultPath(channel.credentials_vault_path || '');
      setSyncEnabled(Boolean(channel.sync_enabled));
      setInboundEnabled(Boolean(channel.inbound_inventory_sync_enabled));
      setOutboundEnabled(Boolean(channel.outbound_inventory_sync_enabled));
      setOrdersWebhookEnabled(Boolean(channel.orders_webhook_enabled));
    }
  }, [channel]);

  if (!isOpen || !channel) return null;

  const handleSave = () => {
    onSave?.({
      credentials_vault_path: vaultPath,
      sync_enabled: syncEnabled,
      inbound_inventory_sync_enabled: inboundEnabled,
      outbound_inventory_sync_enabled: outboundEnabled,
      orders_webhook_enabled: ordersWebhookEnabled,
    });
    onClose();
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.drawer} role="dialog" aria-modal="true">
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Configure {channel.display_name}</h2>
            <p style={styles.subtitle}>Update Vault path and sync controls</p>
          </div>
          <button style={styles.closeButton} onClick={onClose} aria-label="Close drawer">
            ✕
          </button>
        </div>

        <div style={styles.body}>
          <label style={styles.label}>
            Vault Path
            <input
              type="text"
              value={vaultPath}
              onChange={(e) => setVaultPath(e.target.value)}
              placeholder="kv/data/channels/tcgplayer"
              style={styles.input}
            />
          </label>

          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <input
              type="checkbox"
              checked={syncEnabled}
              onChange={(e) => setSyncEnabled(e.target.checked)}
            />
            Enable Sync (master kill-switch)
          </label>

          <div style={styles.sectionDivider}>
            <span style={styles.sectionLabel}>Directional Controls</span>
          </div>

          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <input
              type="checkbox"
              checked={inboundEnabled}
              onChange={(e) => setInboundEnabled(e.target.checked)}
            />
            Inbound inventory sync
          </label>

          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <input
              type="checkbox"
              checked={outboundEnabled}
              onChange={(e) => setOutboundEnabled(e.target.checked)}
            />
            Outbound inventory sync
          </label>

          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <input
              type="checkbox"
              checked={ordersWebhookEnabled}
              onChange={(e) => setOrdersWebhookEnabled(e.target.checked)}
            />
            Orders webhook ingestion
          </label>
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.ghostButton} type="button">
            Cancel
          </button>
          <button onClick={handleSave} style={styles.primaryButton} type="button">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: `${colors.text}26`,
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawer: {
    width: `calc(${spacing.lg} * 10)`,
    maxWidth: '100%',
    height: '100%',
    backgroundColor: colors.background,
    boxShadow: shadows.lg,
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.xs} 0 0 0`,
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
  },
  closeButton: {
    border: 'none',
    background: 'transparent',
    fontSize: typography.h2.fontSize,
    cursor: 'pointer',
    color: colors.text,
  },
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  label: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  input: {
    padding: `${spacing.sm} ${spacing.md}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
  },
  sectionDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  sectionLabel: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap' as const,
  },
  footer: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  ghostButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: 'transparent',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    cursor: 'pointer',
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.background,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    fontWeight: typography.body.fontWeight,
    cursor: 'pointer',
  },
};
